import sum from "lodash/sum";

export type Tranche = {
  tdv: number;
  bdv: number;
  size: number;
};

// A piecewise-linear MVF viewd as a vector of tranches.
export type MVFPL = Tranche[];

export function totalBudget(mvfpl: MVFPL): number {
  return sum(mvfpl.map((tranche) => tranche.size));
}

// Total utility an MVF assigns to a budget. You can plug in big budgets but utility
// stops increasing at the end of the MVF.
export function soloUtility(mvf: MVFPL, grantSize: number): number {
  if (grantSize < 0) {
    throw new Error("Budget must be nonnegative");
  }
  let b = 0;
  let u = 0;

  for (const tranche of mvf) {
    // Add all the utility from this tranche
    if (grantSize >= b + tranche.size) {
      b += tranche.size;
      u += (tranche.size * (tranche.tdv + tranche.bdv)) / 2; // Trapezoid rule is exact for PL functions
    } else {
      const s = (grantSize - b) / tranche.size;
      // average of t.tdv and ((1 - s) * t.tdv + s * t.bdv), the utility of the last dollar distributed
      u +=
        (grantSize - b) * ((1 - s / 2) * tranche.tdv + (s / 2) * tranche.bdv);
      break;
    }
  }

  return u;
}

// To calculate the utility of a speculation grant of size n to project p, take an average,
// weighted by budget, of the utilities each recommender assigns to the first n dollars to p.
// Grants are paid back proportionally to their calculated utilities.
export function weightedUtility(
  mvfs: MVFPL[],
  recommenderBudgets: number[],
  grantSize: number,
): number {
  if (grantSize < 0) {
    throw new Error("Budget must be nonnegative");
  }

  const totalBudget = sum(recommenderBudgets);

  if (totalBudget === 0) {
    return 0;
  }

  return sum(
    mvfs.map(
      (mvf, i) =>
        (soloUtility(mvf, grantSize) * (recommenderBudgets[i] ?? 0)) /
        totalBudget,
    ),
  );
}

// if A gives $100K and B later gives $300K then B's utility will be the difference of the utilities of $400K and $100K.
export function scaleGrantsSequenced(
  mvfs: MVFPL[],
  recommender_budgets: number[],
  grants: number[],
): number[] {
  return grants.map(
    (_, i) =>
      weightedUtility(mvfs, recommender_budgets, sum(grants.slice(0, i + 1))) -
      weightedUtility(mvfs, recommender_budgets, sum(grants.slice(0, i))),
  );
}

// Fold all grants to a given project into one, then split the utility among the grants proportionally.
export function scaleGrantsFold(
  mvfs: MVFPL[],
  recommenderBudgets: number[],
  grants: number[],
): number[] {
  const grantSum = sum(grants);
  const multiplier =
    weightedUtility(mvfs, recommenderBudgets, grantSum) / grantSum;
  return grants.map((g) => g * multiplier);
}

// A combination of scaleGrantsFold and scaleGrantsSequenced.
// sequenceOfSimultaneousGrants is a list of lists of grants that are given simultaneously.
export function scaleGrants(
  mvfs: MVFPL[],
  recommenderBudgets: number[],
  sequenceOfSimultaneousGrants: number[][],
): number[][] {
  let cumulativeSum = 0;

  return sequenceOfSimultaneousGrants.map((parallelGrants) => {
    const prevSum = cumulativeSum;
    const grantSum = sum(parallelGrants);
    cumulativeSum += grantSum;

    const totalUtilityForStep =
      weightedUtility(mvfs, recommenderBudgets, cumulativeSum) -
      weightedUtility(mvfs, recommenderBudgets, prevSum);

    // Scale parallel grants proportionally
    if (grantSum === 0) return parallelGrants.map(() => 0);
    const scaleFactor = totalUtilityForStep / grantSum;
    return parallelGrants.map((grant) => grant * scaleFactor);
  });
}
